import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  LinearProgress,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import DnsRoundedIcon from "@mui/icons-material/DnsRounded";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SettingsEthernetRoundedIcon from "@mui/icons-material/SettingsEthernetRounded";
import { getServerEnv, getServerLogs, getServerOverview, runServerAction, stopServerWindow } from "../../Service/ServerControlService";
import { ToastError, ToastSuccess } from "../../UiComponents/Toaster/Toast";
import StopRunDialog from "../Policy/OnlinePolicy/components/StopRunDialog";
import { actionsFor, actionText, ago, formatBytes, formatDuration, isStale, serverState, TONE, usedPercent, windowStep } from "./serverControlUtils";

const NAVY = "#1E3A5F";
const FONT = "Inter, Poppins, sans-serif";
const MONO = "ui-monospace, Menlo, Consolas, monospace";
const REFRESH_MS = 10000;
const FAST_REFRESH_MS = 3000;

const NOTICE = {
  info: { color: "#175CD3", bg: "#EFF8FF", border: "#B2DDFF", Icon: HourglassTopRoundedIcon },
  ok: { color: "#067647", bg: "#ECFDF3", border: "#ABEFC6", Icon: CheckCircleOutlineRoundedIcon },
  warn: { color: "#B54708", bg: "#FFFAEB", border: "#FEDF89", Icon: WarningAmberRoundedIcon },
  error: { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA", Icon: ErrorOutlineRoundedIcon },
};

/* ------------------------------------------------------------------ small parts ------------------------------------------------------------------ */

/** One notice: an icon, a short bold title, an optional smaller detail, optional buttons on the right. */
const Notice = ({ tone = "info", title, children, actions, spinner, testId }) => {
  const t = NOTICE[tone];
  return (
    <Box
      data-testid={testId}
      sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: "12px 14px", borderRadius: "12px", border: `1px solid ${t.border}`, backgroundColor: t.bg }}
    >
      <Box sx={{ color: t.color, display: "flex", pt: "1px", flexShrink: 0, "& svg": { fontSize: 20 } }}>
        {spinner ? <CircularProgress size={18} sx={{ color: t.color }} /> : <t.Icon />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: t.color, fontFamily: FONT, lineHeight: 1.4 }}>{title}</Typography>
        {children ? <Box sx={{ mt: 0.25, fontSize: "12.5px", color: "#475467", fontFamily: FONT, lineHeight: 1.5 }}>{children}</Box> : null}
      </Box>
      {actions ? <Box sx={{ display: "flex", gap: 1, flexShrink: 0, alignSelf: "center" }}>{actions}</Box> : null}
    </Box>
  );
};

/** A card with a header row (icon, title, something on the right) and a body. */
const Card = ({ title, icon, right, children, testId, sx, bodySx, onHeaderClick, expanded, headerTestId }) => (
  <Box data-testid={testId} sx={{ display: "flex", flexDirection: "column", border: "1px solid #EAECF0", borderRadius: "14px", backgroundColor: "#fff", overflow: "hidden", ...sx }}>
    {title ? (
      <Box
        // a collapsible card: the WHOLE header row opens / closes it (mouse, Enter or Space)
        {...(onHeaderClick
          ? {
              role: "button",
              tabIndex: 0,
              "aria-expanded": !!expanded,
              "data-testid": headerTestId,
              onClick: onHeaderClick,
              onKeyDown: (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onHeaderClick();
                }
              },
            }
          : {})}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          minHeight: 52,
          borderBottom: "1px solid #F2F4F7",
          ...(onHeaderClick
            ? { cursor: "pointer", userSelect: "none", transition: "background-color .15s", "&:hover": { backgroundColor: "#F9FAFB" }, "&:focus-visible": { outline: `2px solid ${NAVY}`, outlineOffset: -2 } }
            : {}),
        }}
      >
        {icon ? (
          <Box sx={{ width: 30, height: 30, borderRadius: "9px", backgroundColor: "#EEF3F9", color: NAVY, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 18 } }}>{icon}</Box>
        ) : null}
        <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#101828", fontFamily: FONT, flex: 1 }}>{title}</Typography>
        {right}
      </Box>
    ) : null}
    <Box sx={{ p: 2, flex: 1, ...bodySx }}>{children}</Box>
  </Box>
);

/** Nothing to show yet: an icon and one line. */
const Empty = ({ icon, text }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 1, py: 3, color: "#98A2B3" }}>
    <Box sx={{ width: 44, height: 44, borderRadius: "50%", backgroundColor: "#F2F4F7", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 22 } }}>{icon}</Box>
    <Typography sx={{ fontSize: "13px", color: "#667085", fontFamily: FONT, maxWidth: 320 }}>{text}</Typography>
  </Box>
);

/** A labelled figure in a soft box. */
const Metric = ({ label, value, sub, testId, wrap = false }) => (
  <Box data-testid={testId} sx={{ minWidth: 0, p: "10px 12px", borderRadius: "10px", backgroundColor: "#F9FAFB", border: "1px solid #F2F4F7" }}>
    <Typography sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#98A2B3", fontFamily: FONT }}>{label}</Typography>
    <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#101828", fontFamily: FONT, lineHeight: 1.35, ...(wrap ? { wordBreak: "break-word" } : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }) }}>{value}</Typography>
    {sub ? <Typography sx={{ fontSize: "11px", color: "#667085", fontFamily: FONT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</Typography> : null}
  </Box>
);

const StatusPill = ({ state, testId, big }) => {
  const t = TONE[state] || TONE.unknown;
  return (
    <Box
      data-testid={testId}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        px: big ? 1.5 : 1.25,
        py: big ? "5px" : "3px",
        borderRadius: "999px",
        border: `1px solid ${t.border}`,
        backgroundColor: t.bg,
        color: t.color,
        fontSize: big ? "13.5px" : "12.5px",
        fontWeight: 700,
        fontFamily: FONT,
        whiteSpace: "nowrap",
      }}
    >
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: t.color, ...(state === "online" ? { boxShadow: `0 0 0 3px ${t.border}` } : {}) }} />
      {t.label}
    </Box>
  );
};

const Bar = ({ label, percent, detail }) => {
  const color = percent >= 90 ? "#B42318" : percent >= 75 ? "#B54708" : "#067647";
  return (
    <Box sx={{ mb: 1.75 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.75, gap: 1 }}>
        <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: "#344054", fontFamily: FONT }}>
          {label} <Box component="span" sx={{ color, fontWeight: 700 }}>{percent}%</Box>
        </Typography>
        <Typography sx={{ fontSize: "11.5px", color: "#667085", fontFamily: FONT }}>{detail}</Typography>
      </Box>
      <LinearProgress variant="determinate" value={percent} sx={{ height: 8, borderRadius: 4, backgroundColor: "#F2F4F7", "& .MuiLinearProgress-bar": { backgroundColor: color, borderRadius: 4 } }} />
    </Box>
  );
};

const STAT_TONE = {
  blue: { color: "#175CD3", bg: "#EFF8FF" },
  gray: { color: "#344054", bg: "#F2F4F7" },
  amber: { color: "#B54708", bg: "#FFFAEB" },
  red: { color: "#B42318", bg: "#FEF3F2" },
};
const Stat = ({ label, value, tone = "gray", icon, testId }) => {
  const t = STAT_TONE[tone];
  return (
    <Box data-testid={testId} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "14px 16px", borderRadius: "14px", border: "1px solid #EAECF0", backgroundColor: "#fff" }}>
      <Box sx={{ width: 40, height: 40, borderRadius: "11px", backgroundColor: t.bg, color: t.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, "& svg": { fontSize: 22 } }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "22px", fontWeight: 700, color: t.color, fontFamily: FONT, lineHeight: 1.15 }}>{value}</Typography>
        <Typography sx={{ fontSize: "12px", color: "#475467", fontFamily: FONT }}>{label}</Typography>
      </Box>
    </Box>
  );
};

/** Start / Stop / Restart: say what will happen, then ask. */
const ConfirmAction = ({ action, running, busy, onCancel, onConfirm }) => {
  if (!action) return null;
  const text = actionText(action, running);
  const color = action === "stop" ? "#B42318" : action === "restart" ? "#B54708" : "#067647";
  const Icon = action === "stop" ? StopRoundedIcon : action === "restart" ? RestartAltRoundedIcon : PlayArrowRoundedIcon;
  return (
    <Dialog open onClose={() => !busy && onCancel()} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <Box sx={{ p: 2.5 }} data-testid="server-action-confirm">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: "11px", backgroundColor: `${color}14`, color, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "15px", color: NAVY, fontFamily: FONT }}>{text.title}</Typography>
        </Box>
        <Box component="ul" sx={{ m: 0, mb: 2, pl: 2.25, "& li": { fontSize: "12.5px", color: "#344054", fontFamily: FONT, lineHeight: 1.6 } }}>
          {text.lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button onClick={onCancel} disabled={busy} sx={{ textTransform: "none", color: "#475467" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={busy}
            data-testid="server-action-go"
            sx={{ textTransform: "none", borderRadius: "8px", backgroundColor: color, boxShadow: "none", "&:hover": { backgroundColor: color, boxShadow: "none", filter: "brightness(0.92)" } }}
          >
            {busy ? "Working…" : text.button}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

/** The current server action (waiting for windows / done / failed / waiting too long). */
const OperationBanner = ({ operation, busy, onForce, onCancelOp }) => {
  if (!operation || operation.phase === "cancelled") return null;
  const waiting = operation.phase === "waiting";
  const timeout = operation.phase === "timeout";
  const running = operation.phase === "running";
  const failed = operation.phase === "failed";
  const verb = operation.action === "stop" ? "Stopping" : operation.action === "restart" ? "Restarting" : "Starting";
  const list = (operation.waitingFor || []).map((w) => [w.customer, w.policyNo, w.part].filter(Boolean).join(" · ")).join(", ");
  const title = waiting
    ? `${verb} - waiting for ${operation.waitingFor.length} window(s) to reach a safe step`
    : running
    ? `${verb} the server…`
    : timeout
    ? `${verb} is waiting - some windows have not reached a safe step yet`
    : failed
    ? `${verb} failed`
    : `${operation.message || "Done"}${operation.finishedAt ? ` · ${ago(operation.finishedAt)}` : ""}`;
  return (
    <Notice
      testId="server-operation"
      tone={failed ? "error" : timeout ? "warn" : waiting || running ? "info" : "ok"}
      spinner={waiting || running}
      title={title}
      actions={
        waiting || timeout ? (
          <>
            <Button size="small" disabled={busy} onClick={onCancelOp} sx={{ textTransform: "none", color: "#475467" }} data-testid="server-op-cancel">
              Cancel
            </Button>
            <Tooltip title="Stops the server now. Windows still running are cut where they are and held when the server starts again." arrow>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disabled={busy}
                  onClick={onForce}
                  data-testid="server-op-force"
                  sx={{ textTransform: "none", borderRadius: "8px", backgroundColor: "#B42318", boxShadow: "none", "&:hover": { backgroundColor: "#912018", boxShadow: "none" } }}
                >
                  Force {operation.action === "restart" ? "restart" : "stop"}
                </Button>
              </span>
            </Tooltip>
          </>
        ) : null
      }
    >
      {(waiting || timeout) && list ? <div>{list}</div> : null}
      {(timeout || failed) && operation.message ? <div>{operation.message}</div> : null}
    </Notice>
  );
};

/* ------------------------------------------------------------------ the page ------------------------------------------------------------------ */

const ServerControl = () => {
  const roleId = sessionStorage.getItem("roleId");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [confirm, setConfirm] = useState(null); // "start" | "stop" | "restart"
  const [busy, setBusy] = useState(false);
  const [stopWindow, setStopWindow] = useState(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState(null);
  const [logTab, setLogTab] = useState("out");
  const [logLines, setLogLines] = useState(200);
  const [envOpen, setEnvOpen] = useState(false);
  const [env, setEnv] = useState(null);
  const [envSearch, setEnvSearch] = useState("");
  const mounted = useRef(true);
  const logsBoxRef = useRef(null);

  const load = useCallback(async () => {
    const res = await getServerOverview();
    if (!mounted.current) return;
    if (res && !res.error) {
      setData(res.data);
      setError(null);
      setUpdatedAt(new Date());
    } else {
      setError((res && res.message) || "Could not load the server status");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (roleId === "SUPER_ADMIN") load();
    return () => {
      mounted.current = false;
    };
  }, [load, roleId]);

  // refresh on its own: every 10 s, every 3 s while a server action is going on
  const waitingOp = data && data.operation && ["waiting", "running"].includes(data.operation.phase);
  useEffect(() => {
    if (roleId !== "SUPER_ADMIN") return undefined;
    const t = setInterval(load, waitingOp ? FAST_REFRESH_MS : REFRESH_MS);
    return () => clearInterval(t);
  }, [load, waitingOp, roleId]);

  // the newest lines are at the bottom: open the log there (and again on another tab / line count / reload)
  useEffect(() => {
    const box = logsBoxRef.current;
    if (box && logs && !logs.error) box.scrollTop = box.scrollHeight;
  }, [logs, logTab, logsOpen]);

  const loadLogs = useCallback(async (lines) => {
    setLogs(null);
    const res = await getServerLogs(lines);
    if (!mounted.current) return;
    setLogs(res && !res.error ? res.data : { error: (res && res.message) || "Could not load the logs" });
  }, []);
  const loadEnv = useCallback(async () => {
    setEnv(null);
    const res = await getServerEnv();
    if (!mounted.current) return;
    setEnv(res && !res.error ? res.data : { error: (res && res.message) || "Could not load the settings" });
  }, []);

  const agent = data && data.agent;
  const state = serverState(agent);
  const can = actionsFor(state);
  const proc = (agent && agent.process) || null;
  const machine = (agent && agent.machine) || null;
  const windows = (data && data.windows) || [];
  const counts = (data && data.counts) || {};
  const opBusy = !!(data && data.operation && ["waiting", "running", "timeout"].includes(data.operation.phase));
  // stop / restart only from a backend on the automation's own database (otherwise it cannot see what runs there)
  const dbCheck = (agent && agent.databaseCheck) || null;
  const dbMismatch = !!(agent && agent.reachable && agent.found && dbCheck && !dbCheck.same);
  const reachable = !!(agent && agent.reachable);
  const pm2Id = (agent && agent.expected && agent.expected.id) || 3;
  const pm2Name = (agent && agent.expected && agent.expected.name) || "server";
  const stateTone = TONE[state] || TONE.unknown;

  const doAction = async (action, opts = {}) => {
    setBusy(true);
    try {
      const res = await runServerAction(action, opts);
      if (res && !res.error) ToastSuccess(res.message || "Done");
      else ToastError((res && res.message) || "The server action failed");
      await load();
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const envRows = useMemo(() => {
    const rows = (env && env.rows) || [];
    const q = envSearch.trim().toLowerCase();
    return q ? rows.filter((r) => `${r.key} ${r.secret ? "" : r.value} ${r.comment || ""}`.toLowerCase().includes(q)) : rows;
  }, [env, envSearch]);

  // Typing /serverControl into the address bar must not open it for anyone else.
  if (roleId !== "SUPER_ADMIN") {
    return (
      <Grid container className="Master_Card_Container">
        <Grid container className="Master_Card_Sub_Container">
          <Typography className="Master_Card_Tittle">Server Control</Typography>
        </Grid>
        <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1, width: "100%" }} />
        <Box sx={{ p: 3 }}>
          <Typography sx={{ color: "#B42318", fontFamily: FONT }}>Only the super admin can use Server Control.</Typography>
        </Box>
      </Grid>
    );
  }

  // the open / closed hint on the right of a collapsible header (the header itself is what is clicked)
  const openHint = (open) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: NAVY, fontSize: "13px", fontWeight: 600, fontFamily: FONT }}>
      {open ? "Hide" : "Show"}
      <ExpandMoreRoundedIcon sx={{ fontSize: 20, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
    </Box>
  );
  const toggleLogs = () => {
    const next = !logsOpen;
    setLogsOpen(next);
    if (next && !logs) loadLogs(logLines);
  };
  const toggleEnv = () => {
    const next = !envOpen;
    setEnvOpen(next);
    if (next && !env) loadEnv();
  };

  return (
    <Grid container className="Master_Card_Container" data-testid="server-control">
      {/* title row */}
      <Grid container alignItems="center" className="Master_Card_Sub_Container" sx={{ gap: 1.5, flexWrap: "nowrap" }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "11px", backgroundColor: NAVY, color: "#fff", display: { xs: "none", sm: "flex" }, alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <DnsRoundedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography className="Master_Card_Tittle">Server Control</Typography>
          <Typography sx={{ fontSize: "12.5px", color: "#667085", fontFamily: FONT }}>
            Look after the automation server - only pm2 #{pm2Id} "{pm2Name}" is touched, no other app on the machine
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "12px", color: "#98A2B3", fontFamily: FONT, whiteSpace: "nowrap", display: { xs: "none", sm: "block" } }}>
          {updatedAt ? `Updated ${ago(updatedAt)}` : ""}
        </Typography>
        <Tooltip title="Read the status again" arrow>
          <span>
            <IconButton onClick={load} disabled={loading} data-testid="server-refresh" sx={{ border: "1px solid #D0D5DD", borderRadius: "10px", width: 38, height: 38 }}>
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
      <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1, width: "100%" }} />

      <Box sx={{ p: { xs: 1.5, sm: 2.5 }, width: "100%", boxSizing: "border-box", backgroundColor: "#FCFCFD" }}>
        {loading && !data ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 6, justifyContent: "center" }}>
            <CircularProgress size={20} sx={{ color: NAVY }} />
            <Typography sx={{ fontSize: "13px", color: "#475467", fontFamily: FONT }}>Reading the server status…</Typography>
          </Box>
        ) : null}
        {error ? (
          <Box sx={{ mb: 2 }}>
            <Notice tone="error" title="Could not load the server status">
              {error}
            </Notice>
          </Box>
        ) : null}

        {data ? (
          <>
            {/* notices: one style, short title, smaller detail */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 2, "&:empty": { display: "none" } }}>
              {agent && !agent.reachable ? (
                <Notice testId="server-unreachable" tone="warn" title={agent.setup ? "Server Control is not set up yet" : "The control agent on the automation server cannot be reached"}>
                  {agent.reason}. The running windows below are read from the database and still work.
                </Notice>
              ) : null}
              {agent && agent.reachable && !agent.found && agent.expected ? (
                <Notice tone="error" title={`pm2 has no process ${agent.expected.id} called "${agent.expected.name}"`}>
                  Nothing can be started or stopped until the agent settings match the pm2 process.
                </Notice>
              ) : null}
              {dbMismatch ? (
                <Notice testId="server-db-mismatch" tone="error" title="Stop and Restart are off - this backend uses a different database than the automation server">
                  {dbCheck.known ? (
                    <>
                      <div>
                        This backend: <b>{dbCheck.own || "unknown"}</b> · automation server: <b>{dbCheck.automation}</b>.
                      </div>
                      <div>It cannot see or hold the policies running there, so stopping could cut them half way. Use Server Control from the backend that uses the automation's database.</div>
                    </>
                  ) : (
                    "The automation server's control agent did not say which database it uses - update the agent on the server."
                  )}
                </Notice>
              ) : null}
              <OperationBanner
                operation={data.operation}
                busy={busy}
                onForce={() => doAction(data.operation.action === "restart" ? "restart" : "stop", { force: true })}
                onCancelOp={() => doAction("cancel")}
              />
            </Box>

            <Grid container spacing={2}>
              {/* the server — the big card */}
              <Grid item xs={12} lg={8}>
                <Card testId="server-card" sx={{ height: "100%" }} bodySx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Box
                      sx={{ width: 52, height: 52, borderRadius: "14px", backgroundColor: stateTone.bg, border: `1px solid ${stateTone.border}`, color: stateTone.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, "& svg": { fontSize: 28 } }}
                    >
                      <DnsRoundedIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 180 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "17px", color: "#101828", fontFamily: FONT }}>Automation server</Typography>
                        <StatusPill state={state} testId="server-status" big />
                      </Box>
                      <Typography sx={{ fontSize: "12.5px", color: "#667085", fontFamily: FONT, mt: 0.25 }}>
                        pm2 #{pm2Id} · {pm2Name}
                        {proc && state === "online" ? ` · up ${formatDuration(proc.uptimeMs)}` : ""}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        startIcon={<PlayArrowRoundedIcon />}
                        disabled={!can.start || busy || opBusy || !reachable}
                        onClick={() => setConfirm("start")}
                        data-testid="server-start"
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 2, backgroundColor: "#067647", boxShadow: "none", "&:hover": { backgroundColor: "#05603A", boxShadow: "none" } }}
                      >
                        Start
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<RestartAltRoundedIcon />}
                        disabled={!can.restart || busy || opBusy || !reachable || dbMismatch}
                        onClick={() => setConfirm("restart")}
                        data-testid="server-restart"
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 2, color: "#B54708", borderColor: "#FEDF89", "&:hover": { borderColor: "#B54708", backgroundColor: "#FFFAEB" } }}
                      >
                        Restart
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<StopRoundedIcon />}
                        disabled={!can.stop || busy || opBusy || !reachable || dbMismatch}
                        onClick={() => setConfirm("stop")}
                        data-testid="server-stop"
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 2, color: "#B42318", borderColor: "#FECDCA", "&:hover": { borderColor: "#B42318", backgroundColor: "#FEF3F2" } }}
                      >
                        Stop
                      </Button>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2, borderColor: "#F2F4F7" }} />

                  {proc ? (
                    <>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", md: "repeat(6, minmax(0, 1fr))" }, gap: 1.25 }}>
                        <Metric label="Uptime" value={state === "online" ? formatDuration(proc.uptimeMs) : "—"} sub={proc.startedAt && state === "online" ? `since ${new Date(proc.startedAt).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : null} testId="fact-uptime" />
                        <Metric label="Restarts" value={proc.restarts} sub={proc.unstableRestarts ? `${proc.unstableRestarts} unstable` : null} />
                        <Metric label="CPU" value={`${proc.cpu}%`} />
                        <Metric label="Memory" value={formatBytes(proc.memoryBytes)} />
                        <Metric label="PID" value={proc.pid || "—"} />
                        <Metric label="Node" value={proc.nodeVersion || "—"} />
                      </Box>
                      {proc.script || proc.cwd ? (
                        <Typography sx={{ mt: 1.5, fontSize: "11.5px", color: "#98A2B3", fontFamily: MONO, wordBreak: "break-all" }}>{proc.script || proc.cwd}</Typography>
                      ) : null}
                    </>
                  ) : (
                    <Empty
                      icon={<SettingsEthernetRoundedIcon />}
                      text={reachable ? "pm2 did not report the automation server." : "Uptime, restarts, CPU and memory appear here when the control agent answers."}
                    />
                  )}
                </Card>
              </Grid>

              {/* the machine */}
              <Grid item xs={12} lg={4}>
                <Card testId="machine-card" title="Machine" icon={<MemoryRoundedIcon />} sx={{ height: "100%" }}>
                  {machine ? (
                    <>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.25, mb: 2 }}>
                        <Metric label="Host" value={machine.hostname} sub={machine.platform} wrap />
                        <Metric wrap label="Load 1 / 5 / 15 min" value={(machine.loadavg || []).map((n) => Number(n).toFixed(2)).join(" / ")} sub={`${machine.cpus} CPU · up ${formatDuration(machine.uptimeSec * 1000)}`} />
                      </Box>
                      <Bar label="Memory" percent={usedPercent(machine.totalMemBytes, machine.freeMemBytes)} detail={`${formatBytes(machine.totalMemBytes - machine.freeMemBytes)} of ${formatBytes(machine.totalMemBytes)}`} />
                      {machine.disk ? (
                        <Bar label="Disk" percent={usedPercent(machine.disk.totalBytes, machine.disk.freeBytes)} detail={`${formatBytes(machine.disk.freeBytes)} free of ${formatBytes(machine.disk.totalBytes)}`} />
                      ) : null}
                    </>
                  ) : (
                    <Empty icon={<MemoryRoundedIcon />} text="Load, memory and disk appear here when the control agent answers." />
                  )}
                </Card>
              </Grid>

              {/* the queue */}
              <Grid item xs={12}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 2 }}>
                  <Stat label="Running now" value={counts.processing ?? 0} tone="blue" icon={<PlayCircleOutlineRoundedIcon />} testId="count-running" />
                  <Stat label="Waiting in the queue" value={counts.pending ?? 0} tone="gray" icon={<HourglassTopRoundedIcon />} testId="count-pending" />
                  <Stat label="On hold" value={counts.hold ?? 0} tone="amber" icon={<PauseRoundedIcon />} testId="count-hold" />
                  <Stat label="Failed today" value={counts.failedToday ?? 0} tone={counts.failedToday ? "red" : "gray"} icon={<ReportProblemOutlinedIcon />} testId="count-failed" />
                </Box>
              </Grid>

              {/* running windows */}
              <Grid item xs={12}>
                <Card
                  testId="windows-card"
                  title="Running windows"
                  icon={<PauseCircleOutlineIcon />}
                  right={<Chip size="small" label={windows.length} sx={{ height: 22, fontWeight: 700, fontFamily: FONT, backgroundColor: windows.length ? "#EFF8FF" : "#F2F4F7", color: windows.length ? "#175CD3" : "#475467" }} />}
                  bodySx={{ p: windows.length ? 0 : 2 }}
                >
                  {windows.length === 0 ? (
                    <Empty icon={<PauseCircleOutlineIcon />} text="No policy is being processed right now." />
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: { xs: "none", md: "grid" },
                          gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.3fr) 140px",
                          gap: 2,
                          px: 2,
                          py: 1,
                          backgroundColor: "#F9FAFB",
                          borderBottom: "1px solid #EAECF0",
                          "& p": { fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#667085", fontFamily: FONT },
                        }}
                      >
                        <Typography>Customer · policy</Typography>
                        <Typography>Working on</Typography>
                        <Typography>Running</Typography>
                        <Typography sx={{ textAlign: "right" }}>Action</Typography>
                      </Box>
                      {windows.map((w, i) => {
                        const stale = isStale(w.lastHeartbeatAt);
                        const step = windowStep(w);
                        return (
                          <Box
                            key={w.jobId}
                            data-testid={`window-${w.policyId}`}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.3fr) 140px" },
                              gap: { xs: 1, md: 2 },
                              alignItems: "center",
                              px: 2,
                              py: 1.5,
                              borderTop: i ? "1px solid #F2F4F7" : "none",
                              "&:hover": { backgroundColor: "#FCFCFD" },
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: "#101828", fontFamily: FONT }}>{w.customer || "—"}</Typography>
                              <Typography sx={{ fontSize: "12px", color: "#667085", fontFamily: FONT }}>
                                {[w.policyNo, w.company && String(w.company).toUpperCase(), w.part && w.part.label].filter(Boolean).join(" · ")}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                              <Chip size="small" label={step ? `Working on ${step}` : "Working"} sx={{ height: 24, fontSize: "12px", fontWeight: 600, color: TONE.starting.color, backgroundColor: TONE.starting.bg, border: `1px solid ${TONE.starting.border}` }} />
                              {w.holdRequested ? (
                                <Chip size="small" label="Stopping - hold requested" sx={{ height: 24, fontSize: "12px", fontWeight: 600, color: TONE.stopping.color, backgroundColor: TONE.stopping.bg, border: `1px solid ${TONE.stopping.border}` }} />
                              ) : null}
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: "12.5px", color: "#344054", fontFamily: FONT }}>{w.startedAt ? `for ${ago(w.startedAt).replace(" ago", "")}` : "—"}</Typography>
                              <Typography sx={{ fontSize: "11.5px", color: stale ? "#B42318" : "#667085", fontFamily: FONT, fontWeight: stale ? 700 : 400 }}>
                                Last sign of life {ago(w.lastHeartbeatAt)}
                                {stale ? " - may be stuck" : ""}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PauseCircleOutlineIcon />}
                                disabled={!!w.holdRequested}
                                onClick={() => setStopWindow({ _id: w.policyId, fullName: w.customer, policyId: w.policyNo, Paymentmethod: w.Paymentmethod, relianceStageView: w.relianceStageView, statusLabel: "In progress" })}
                                data-testid={`window-stop-${w.policyId}`}
                                sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", color: "#B54708", borderColor: "#FEDF89", whiteSpace: "nowrap", "&:hover": { borderColor: "#B54708", backgroundColor: "#FFFAEB" } }}
                              >
                                Stop &amp; hold
                              </Button>
                            </Box>
                          </Box>
                        );
                      })}
                    </>
                  )}
                </Card>
              </Grid>

              {/* logs */}
              <Grid item xs={12}>
                <Card
                  testId="logs-card"
                  title="Logs"
                  icon={<TerminalRoundedIcon />}
                  right={openHint(logsOpen)}
                  onHeaderClick={toggleLogs}
                  expanded={logsOpen}
                  headerTestId="logs-toggle"
                  bodySx={{ py: logsOpen ? 2 : 1.5 }}
                >
                  <Collapse in={logsOpen} unmountOnExit>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25, flexWrap: "wrap" }}>
                      <Tabs value={logTab} onChange={(e, v) => setLogTab(v)} sx={{ minHeight: 34, "& .MuiTab-root": { minHeight: 34, textTransform: "none", fontFamily: FONT, fontWeight: 600 } }}>
                        <Tab value="out" label="Output" />
                        <Tab value="err" label="Errors" />
                      </Tabs>
                      <Box sx={{ flex: 1 }} />
                      {[100, 200, 500].map((n) => (
                        <Chip
                          key={n}
                          size="small"
                          label={`${n} lines`}
                          onClick={() => {
                            setLogLines(n);
                            loadLogs(n);
                          }}
                          sx={{ fontFamily: FONT, fontWeight: 600, ...(logLines === n ? { backgroundColor: NAVY, color: "#fff", "&:hover": { backgroundColor: NAVY } } : {}) }}
                        />
                      ))}
                      <Tooltip title="Read the logs again" arrow>
                        <IconButton size="small" onClick={() => loadLogs(logLines)} aria-label="Reload logs">
                          <RefreshRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {!logs ? (
                      <LinearProgress sx={{ my: 1 }} />
                    ) : logs.error ? (
                      <Notice tone="error" title="Could not load the logs">
                        {logs.error}
                      </Notice>
                    ) : (
                      <Box
                        ref={logsBoxRef}
                        data-testid="logs-box"
                        sx={{ maxHeight: 420, overflow: "auto", backgroundColor: "#0B1220", color: "#D0D5DD", borderRadius: "10px", p: 1.5, fontFamily: MONO, fontSize: "12px", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        {(logs[logTab] || []).length ? (logs[logTab] || []).join("\n") : "(empty)"}
                      </Box>
                    )}
                  </Collapse>
                  {!logsOpen ? <Typography sx={{ fontSize: "12.5px", color: "#667085", fontFamily: FONT }}>The last lines of the server's output and error logs.</Typography> : null}
                </Card>
              </Grid>

              {/* settings */}
              <Grid item xs={12}>
                <Card
                  testId="env-card"
                  title="Settings (.env)"
                  icon={<LockRoundedIcon />}
                  right={openHint(envOpen)}
                  onHeaderClick={toggleEnv}
                  expanded={envOpen}
                  headerTestId="env-toggle"
                  bodySx={{ py: envOpen ? 2 : 1.5 }}
                >
                  <Collapse in={envOpen} unmountOnExit>
                    <Input
                      className="w-100 Master_Card_Input"
                      disableUnderline
                      placeholder="Search a setting"
                      value={envSearch}
                      onChange={(e) => setEnvSearch(e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <SearchRoundedIcon fontSize="small" />
                        </InputAdornment>
                      }
                      sx={{ mb: 1.5, maxWidth: 360 }}
                      inputProps={{ "data-testid": "env-search" }}
                    />
                    {!env ? (
                      <LinearProgress sx={{ my: 1 }} />
                    ) : env.error ? (
                      <Notice tone="error" title="Could not load the settings">
                        {env.error}
                      </Notice>
                    ) : (
                      <Box data-testid="env-rows" sx={{ border: "1px solid #EAECF0", borderRadius: "10px", overflow: "hidden" }}>
                        <Box
                          sx={{
                            display: { xs: "none", md: "grid" },
                            gridTemplateColumns: "minmax(220px, 30%) minmax(140px, 20%) 1fr",
                            gap: 2,
                            px: 1.5,
                            py: 1,
                            backgroundColor: "#F9FAFB",
                            borderBottom: "1px solid #EAECF0",
                            "& p": { fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#667085", fontFamily: FONT },
                          }}
                        >
                          <Typography>Setting</Typography>
                          <Typography>Value</Typography>
                          <Typography>What it is</Typography>
                        </Box>
                        {envRows.map((r, i) => (
                          <Box
                            key={r.key}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 30%) minmax(140px, 20%) 1fr" },
                              gap: { xs: 0.25, md: 2 },
                              alignItems: "center",
                              px: 1.5,
                              py: "9px",
                              backgroundColor: i % 2 ? "#FAFBFC" : "#fff",
                              borderTop: i ? "1px solid #F2F4F7" : "none",
                            }}
                          >
                            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: NAVY, fontFamily: MONO, wordBreak: "break-all" }}>{r.key}</Typography>
                            <Box sx={{ minWidth: 0 }}>
                              {r.secret ? (
                                <Chip size="small" icon={<LockRoundedIcon sx={{ fontSize: "14px !important" }} />} label="hidden" sx={{ height: 22, fontSize: "11.5px", color: "#475467" }} />
                              ) : (
                                <Typography sx={{ fontSize: "12.5px", color: "#101828", fontFamily: MONO, wordBreak: "break-all" }}>{r.value === "" ? "(empty)" : r.value}</Typography>
                              )}
                            </Box>
                            <Typography sx={{ fontSize: "12px", color: "#667085", fontFamily: FONT }}>{r.comment || ""}</Typography>
                          </Box>
                        ))}
                        {!envRows.length ? <Typography sx={{ p: 1.5, fontSize: "12.5px", color: "#667085", fontFamily: FONT }}>No setting matches.</Typography> : null}
                      </Box>
                    )}
                  </Collapse>
                  {!envOpen ? (
                    <Typography sx={{ fontSize: "12.5px", color: "#667085", fontFamily: FONT }}>
                      Every setting of the automation's .env, read only. Keys, passwords, tokens and addresses are hidden.
                    </Typography>
                  ) : null}
                </Card>
              </Grid>
            </Grid>
          </>
        ) : null}
      </Box>

      <ConfirmAction action={confirm} running={windows.length} busy={busy} onCancel={() => setConfirm(null)} onConfirm={() => doAction(confirm)} />

      <StopRunDialog
        policy={stopWindow}
        onCancel={() => setStopWindow(null)}
        onConfirm={async (p) => {
          const res = await stopServerWindow(p._id);
          if (res && !res.error) ToastSuccess(res.message || "The window stops at its next safe step");
          else ToastError((res && res.message) || "Could not stop this window");
          setStopWindow(null);
          await load();
        }}
      />
    </Grid>
  );
};

export default ServerControl;
